import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Check, ChevronDown, Clock, MessageSquare, User } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { useTeamContext } from '@/context/TeamContext'
import { useProgramContext } from '@/context/ProgramContext'
import { apiClient } from '@/lib/api'
import { cn } from '@/lib/utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from '@/components/ui/use-toast'
import {
	useAgentStatusRealtime,
	useActivityTracker,
} from '@/hooks/useAgentStatusRealtime'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { useTranslation } from '@/hooks/useTranslation'

type AgentStatus = 'online' | 'away' | 'offline'

interface AgentStatusData {
	user_id: string
	status: AgentStatus
	status_message: string | null
	last_active_at: string | null
}

const getStatusConfig = (t: (key: string) => string) => ({
	online: {
		label: t('agentStatusIndicator.online'),
		color: 'bg-green-500',
		textColor: 'text-green-700',
		hoverBg: 'hover:bg-green-50',
		pulse: 'animate-pulse',
	},
	away: {
		label: t('agentStatusIndicator.away'),
		color: 'bg-amber-500',
		textColor: 'text-amber-700',
		hoverBg: 'hover:bg-amber-50',
		pulse: '',
	},
	offline: {
		label: t('agentStatusIndicator.offline'),
		color: 'bg-slate-400',
		textColor: 'text-slate-600',
		hoverBg: 'hover:bg-slate-50',
		pulse: '',
	},
})

// Status Indicator Component
interface StatusDotProps {
	status: AgentStatus
	size?: 'sm' | 'md'
}

const StatusDot: React.FC<StatusDotProps & { statusConfig: ReturnType<typeof getStatusConfig> }> = ({ status, size = 'md', statusConfig }) => {
	const config = statusConfig[status]
	const dotSize = size === 'sm' ? 'h-2 w-2' : 'h-2.5 w-2.5'
	const ringSize = size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'
	
	return (
		<div className="relative flex items-center justify-center">
			{/* Outer ring for online status */}
			{status === 'online' && (
				<div className={cn('absolute rounded-full bg-green-500/20', ringSize, config.pulse)} />
			)}
			{/* Status dot */}
			<div className={cn('rounded-full', config.color, dotSize)} />
		</div>
	)
}

// Loading Skeleton Component
const StatusSkeleton: React.FC = () => (
	<div className="flex items-center gap-2">
		<Skeleton className="h-2.5 w-2.5 rounded-full" />
		<Skeleton className="h-4 w-16" />
		<Skeleton className="h-4 w-4" />
	</div>
)

function useEffectiveProgramId(programId?: string) {
	let contextProgramId: string | null = null
	try {
		const programContext = useProgramContext()
		contextProgramId = programContext?.programId || null
	} catch {
		// Not within ProgramProvider, that's ok
	}
	return programId || contextProgramId
}

export function AgentPresenceTracker() {
	const { user } = useAuth()
	const { currentTeam } = useTeamContext()
	const effectiveProgramId = useEffectiveProgramId()

	useAgentStatusRealtime(currentTeam?.id, effectiveProgramId || undefined)
	useActivityTracker(user?.id, effectiveProgramId || undefined)

	return null
}

interface AgentStatusIndicatorProps {
	programId?: string
}

export function AgentStatusIndicator({ programId }: AgentStatusIndicatorProps = {}) {
	const { t } = useTranslation()
	const { user } = useAuth()
	const queryClient = useQueryClient()
	const navigate = useNavigate()
	const [statusMessage, setStatusMessage] = useState('')
	const [showStatusMessage, setShowStatusMessage] = useState(false)
	const statusConfig = getStatusConfig(t)
	const effectiveProgramId = useEffectiveProgramId(programId)

	// Fetch current agent status
	const { data: agentStatus, isLoading } = useQuery({
		queryKey: ['agent-status', user?.id, effectiveProgramId],
		queryFn: async () => {
			if (!effectiveProgramId) {
				return null
			}
			const response = await apiClient<AgentStatusData>(
				`/api/agent-status/status?program_id=${effectiveProgramId}`
			)
			return response as AgentStatusData
		},
		enabled: !!user && !!effectiveProgramId,
		refetchInterval: 30000, // Refresh every 30 seconds
	})

	// Update agent status mutation
	const updateStatusMutation = useMutation({
		mutationFn: async (data: {
			status: AgentStatus
			status_message?: string | null
		}) => {
			if (!effectiveProgramId) {
				throw new Error('Program ID is required')
			}
			const response = await apiClient(`/api/agent-status/status?program_id=${effectiveProgramId}`, {
				method: 'PUT',
				body: JSON.stringify(data),
			})
			return response
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['agent-status'] })
			toast({
				title: t('agentStatusIndicator.success'),
				description: t('agentStatusIndicator.statusUpdatedSuccessfully'),
			})
			setShowStatusMessage(false)
		},
		onError: () => {
			toast({
				title: t('agentStatusIndicator.error'),
				description: t('agentStatusIndicator.failedToUpdateStatus'),
				variant: 'destructive',
			})
		},
	})

	// Check for inactivity
	const checkInactivityMutation = useMutation({
		mutationFn: async () => {
			if (!effectiveProgramId) {
				return null
			}
			const response = await apiClient(`/api/agent-status/check-inactivity?program_id=${effectiveProgramId}`, {
				method: 'POST',
			})
			return response
		},
		onSuccess: (data) => {
			if (data.status_changed) {
				queryClient.invalidateQueries({ queryKey: ['agent-status'] })
			}
		},
	})

	// Auto-check inactivity every 2 minutes
	useEffect(() => {
		const interval = setInterval(() => {
			if (agentStatus?.status === 'online') {
				checkInactivityMutation.mutate()
			}
		}, 2 * 60 * 1000)

		return () => clearInterval(interval)
	}, [agentStatus?.status])

	// Initialize status message
	useEffect(() => {
		if (agentStatus?.status_message) {
			setStatusMessage(agentStatus.status_message)
		}
	}, [agentStatus?.status_message])

	const handleStatusChange = (newStatus: AgentStatus) => {
		if (newStatus === 'away') {
			setShowStatusMessage(true)
		} else {
			updateStatusMutation.mutate({ status: newStatus, status_message: null })
		}
	}

	const handleAwayWithMessage = () => {
		updateStatusMutation.mutate({
			status: 'away',
			status_message: statusMessage || null,
		})
	}

	if (isLoading || !agentStatus) {
		return <StatusSkeleton />
	}

	const currentStatus = agentStatus.status
	const currentStatusConfig = statusConfig[currentStatus]
	const userInitials = user?.user_metadata?.full_name
		?.split(' ')
		.map((n: string) => n[0])
		.join('')
		.toUpperCase()
		.slice(0, 2) || 'AG'

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button 
					variant="ghost" 
					size="sm"
					className="h-auto p-2 gap-2 hover:bg-slate-50 data-[state=open]:bg-slate-50"
				>
					<StatusDot status={currentStatus} statusConfig={statusConfig} />
					<span className={cn("text-sm font-medium", currentStatusConfig.textColor)}>
						{currentStatusConfig.label}
					</span>
					<ChevronDown className="h-3 w-3 text-slate-400" />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end" className="w-80">
				<DropdownMenuLabel className="p-4">
					<div className="flex items-center gap-3">
						<Avatar className="h-10 w-10">
							<AvatarImage src={user?.user_metadata?.avatar_url} alt="Avatar" />
							<AvatarFallback className="text-sm font-medium">
								{userInitials}
							</AvatarFallback>
						</Avatar>
						<div className="flex-1 min-w-0">
							<p className="text-sm font-semibold text-slate-900 truncate">
								{user?.user_metadata?.full_name || user?.email?.split('@')[0] || t('agentStatusIndicator.teamMember')}
							</p>
							<p className="text-xs text-slate-500 truncate">{user?.email}</p>
							{agentStatus.status_message && agentStatus.status === 'away' && (
								<p className="text-xs text-amber-600 truncate mt-1">
									{agentStatus.status_message}
								</p>
							)}
						</div>
					</div>
				</DropdownMenuLabel>
				
				<DropdownMenuSeparator />

				<div className="p-1">
					{Object.entries(statusConfig).map(([statusKey, config]) => (
						<DropdownMenuItem
							key={statusKey}
							onClick={() => handleStatusChange(statusKey as AgentStatus)}
							onSelect={(e) => {
								// Prevent dropdown from closing when selecting "Away"
								if (statusKey === 'away') {
									e.preventDefault()
								}
							}}
							className="flex items-center justify-between p-3 cursor-pointer"
						>
							<div className="flex items-center gap-3">
								<StatusDot status={statusKey as AgentStatus} size="sm" statusConfig={statusConfig} />
								<span className="text-sm font-medium">{config.label}</span>
							</div>
							{currentStatus === statusKey && (
								<Check className="h-4 w-4 text-green-600" />
							)}
						</DropdownMenuItem>
					))}
				</div>

				{showStatusMessage && (
					<>
						<DropdownMenuSeparator />
						<div className="p-4 space-y-3">
							<div className="space-y-2">
								<label htmlFor="status-message" className="text-xs font-medium text-slate-700">
									{t('agentStatusIndicator.statusMessage')}
								</label>
								<Input
									id="status-message"
									type="text"
									placeholder={t('agentStatusIndicator.statusMessagePlaceholder')}
									value={statusMessage}
									onChange={(e) => setStatusMessage(e.target.value)}
									className="text-sm"
									maxLength={50}
								/>
							</div>
							<div className="flex gap-2">
								<Button
									variant="outline"
									size="sm"
									onClick={() => setShowStatusMessage(false)}
									className="flex-1"
								>
									{t('agentStatusIndicator.cancel')}
								</Button>
								<Button
									onClick={handleAwayWithMessage}
									size="sm"
									className="flex-1 bg-amber-600 hover:bg-amber-700"
									disabled={updateStatusMutation.isPending}
								>
									{updateStatusMutation.isPending ? t('agentStatusIndicator.setting') : t('agentStatusIndicator.setAway')}
								</Button>
							</div>
						</div>
					</>
				)}

				<DropdownMenuSeparator />
				
				<div className="p-1">
					<DropdownMenuItem
						onClick={() => navigate('/live-chat?tab=agent')}
						className="p-3 cursor-pointer"
					>
						<User className="mr-3 h-4 w-4 text-slate-500" />
						<span className="text-sm">{t('agentStatusIndicator.editProfile')}</span>
					</DropdownMenuItem>
					<DropdownMenuItem
						onClick={() => navigate('/inbox')}
						className="p-3 cursor-pointer"
					>
						<MessageSquare className="mr-3 h-4 w-4 text-slate-500" />
						<span className="text-sm">{t('agentStatusIndicator.activeChats')}</span>
					</DropdownMenuItem>
				</div>
			</DropdownMenuContent>
		</DropdownMenu>
	)
}